// src/utils/pdfGenerator.js - SIMPLIFIED VERSION
import { jsPDF } from 'jspdf';
import { toastSuccess, toastError } from './toast';
import { format } from 'date-fns';
import { debugLog, debugError } from './debug';

// Helper function to format onboarding data for display
const formatOnboardingData = (data) => {
  debugLog('formatOnboardingData - Input data:', data);
  debugLog('formatOnboardingData - Data keys:', Object.keys(data || {}));
  debugLog('formatOnboardingData - Full data structure:', JSON.stringify(data, null, 2));
  if (!data) return null;
  
  const sections = [];
  
  // Helper function to format time from seconds or time object
  const formatTime = (time) => {
    debugLog('formatTime - Input:', time, 'Type:', typeof time);
    
    // Handle null, undefined, or empty values
    if (time === null || time === undefined || time === '') {
      debugLog('formatTime - Null/undefined/empty input, returning null');
      return null;
    }
    
    if (typeof time === 'string') {
      debugLog('formatTime - String input, returning as-is:', time);
      // Validate that it looks like a time string (HH:MM:SS or MM:SS format)
      if (time.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
        return time;
      } else {
        debugLog('formatTime - String does not match time format, returning as-is:', time);
        return time;
      }
    }
    
    if (typeof time === 'number') {
      debugLog('formatTime - Number input, converting from seconds:', time);
      // Ensure it's a positive number
      if (time < 0) {
        debugLog('formatTime - Negative number, returning null');
        return null;
      }
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      const seconds = time % 60;
      const paddedSeconds = seconds.toString().padStart(2, '0');
      const paddedMinutes = minutes.toString().padStart(2, '0');
      const result = hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
      debugLog('formatTime - Converted result:', result);
      return result;
    }
    
    if (time && typeof time === 'object') {
      if (time.totalSeconds !== undefined) {
        debugLog('formatTime - Object with totalSeconds, converting:', time.totalSeconds);
        const totalSeconds = time.totalSeconds;
        if (totalSeconds < 0) {
          debugLog('formatTime - Negative totalSeconds, returning null');
          return null;
        }
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const paddedSeconds = seconds.toString().padStart(2, '0');
        const paddedMinutes = minutes.toString().padStart(2, '0');
        const result = hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
        debugLog('formatTime - Converted result:', result);
        return result;
      }
      
      if (time.hours !== undefined || time.minutes !== undefined) {
        debugLog('formatTime - Object with hours/minutes:', time);
        const hours = time.hours || 0;
        const minutes = time.minutes || 0;
        if (hours > 0 || minutes > 0) {
          const result = `${hours}h ${minutes}m`;
          debugLog('formatTime - Converted result:', result);
          return result;
        }
      }
    }
    
    debugLog('formatTime - No valid format found, returning null');
    return null;
  };
  
  // Helper function to get all possible running reasons
  const getAllRunningReasons = () => [
    'train_for_a_race',
    'achieve_personal_best', 
    'improve_fitness',
    'lose_weight',
    'stay_healthy',
    'run_for_enjoyment'
  ];
  
  const reasonLabels = {
    'train_for_a_race': 'Train for a race',
    'achieve_personal_best': 'Achieve personal best',
    'improve_fitness': 'Improve fitness',
    'lose_weight': 'Lose weight',
    'stay_healthy': 'Stay healthy',
    'run_for_enjoyment': 'Run for enjoyment'
  };
  
  // Running reasons - show all possible options
  const allReasons = getAllRunningReasons();
  const selectedReasons = data.runningReasons || [];
  const reasonTexts = allReasons.map(reason => {
    const isSelected = selectedReasons.includes(reason);
    return `${reasonLabels[reason]}: ${isSelected ? 'Yes' : 'null'}`;
  });
  sections.push(`Goals: ${reasonTexts.join(', ')}`);
  
  // Personal Bests - handle the actual data structure from database
  debugLog('PDF Generator - Full data structure:', JSON.stringify(data, null, 2));
  debugLog('PDF Generator - Data keys:', Object.keys(data || {}));
  
  // Check if we have personalBests or if the data is stored directly
  let pbs = data.personalBests || data;
  debugLog('PDF Generator - Personal Bests data:', pbs);
  debugLog('PDF Generator - Personal Bests structure:', JSON.stringify(pbs, null, 2));
  
  // Check if we have personal bests data in any format
  if (pbs && (pbs.lifetimePB || pbs.recentPB || pbs.lifetimePBs || pbs.recentPBs || pbs.lifetimePBs || pbs.recentPBs)) {
    const allDistances = ['5K', '10K', 'Half Marathon', 'Marathon', 'Other Distance'];
    
    // Lifetime PBs - handle the actual data structure
    const lifetimePBs = [];
    allDistances.forEach(distance => {
      let pb = null;
      let year = null;
      
      // Check if we have the old structure with lifetimePB as a string (formatted)
      if (pbs.lifetimePB) {
        if (typeof pbs.lifetimePB === 'string' && pbs.lifetimePB.includes('Marathon')) {
          // Extract marathon time from string like "Marathon: 3:11:10 (2022)"
          const match = pbs.lifetimePB.match(/Marathon: ([^(]+)(?: \((\d+)\))?/);
          if (match && distance === 'Marathon') {
            pb = match[1].trim();
            year = match[2];
          }
        }
      }
      // Check if we have the new structure with lifetimePBs (raw seconds)
      else if (pbs.lifetimePBs && pbs.lifetimePBs[distance]) {
        pb = pbs.lifetimePBs[distance];
        year = pbs.lifetimeYears?.[distance];
      }
      // Check if we have raw seconds data in the database structure
      else if (pbs.lifetimePBs && typeof pbs.lifetimePBs[distance] === 'number') {
        pb = pbs.lifetimePBs[distance];
        year = pbs.lifetimeYears?.[distance];
      }
      
      debugLog(`PDF Generator - ${distance} lifetime PB:`, pb, 'year:', year);
      debugLog(`PDF Generator - ${distance} lifetime PB type:`, typeof pb);
      
      // Check if this is a world record time that shouldn't be displayed
      if (distance === '5K' && pb === '12:35') {
        debugLog('PDF Generator - Detected world record 5K time, setting to null');
        lifetimePBs.push(`${distance}: null`);
        return;
      }
      
      if (pb && pb !== null && pb !== undefined && pb !== '') {
        debugLog(`PDF Generator - Before formatTime - ${distance} PB:`, pb, 'Type:', typeof pb);
        const timeStr = formatTime(pb);
        debugLog(`PDF Generator - After formatTime - ${distance} formatted time:`, timeStr);
        
        // Force conversion if formatTime didn't work but we have a number
        if (timeStr === null && typeof pb === 'number' && pb > 0) {
          const hours = Math.floor(pb / 3600);
          const minutes = Math.floor((pb % 3600) / 60);
          const seconds = pb % 60;
          const paddedSeconds = seconds.toString().padStart(2, '0');
          const paddedMinutes = minutes.toString().padStart(2, '0');
          const forcedTime = hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
          debugLog(`PDF Generator - Force converted ${distance} time:`, forcedTime);
          const yearStr = year ? ` (${year})` : '';
          lifetimePBs.push(`${distance}: ${forcedTime}${yearStr}`);
        } else if (timeStr) {
          const yearStr = year ? ` (${year})` : '';
          lifetimePBs.push(`${distance}: ${timeStr}${yearStr}`);
        } else {
          lifetimePBs.push(`${distance}: ${pb}${year ? ` (${year})` : ''}`);
        }
      } else {
        lifetimePBs.push(`${distance}: null`);
      }
    });
    sections.push(`Lifetime PB: ${lifetimePBs.join(', ')}`);
    
    // Recent PBs (12 months) - handle the actual data structure
    const recentPBs = [];
    allDistances.forEach(distance => {
      let pb = null;
      let year = null;
      
      // Check if we have the old structure with recentPB as a string (formatted)
      if (pbs.recentPB) {
        if (typeof pbs.recentPB === 'string' && pbs.recentPB.includes('Marathon')) {
          // Extract marathon time from string like "Marathon: 3:24:13 (2025)"
          const match = pbs.recentPB.match(/Marathon: ([^(]+)(?: \((\d+)\))?/);
          if (match && distance === 'Marathon') {
            pb = match[1].trim();
            year = match[2];
          }
        }
      }
      // Check if we have the new structure with recentPBs (raw seconds)
      else if (pbs.recentPBs && pbs.recentPBs[distance]) {
        pb = pbs.recentPBs[distance];
        year = pbs.recentYears?.[distance];
      }
      // Check if we have raw seconds data in the database structure
      else if (pbs.recentPBs && typeof pbs.recentPBs[distance] === 'number') {
        pb = pbs.recentPBs[distance];
        year = pbs.recentYears?.[distance];
      }
      
      debugLog(`PDF Generator - ${distance} recent PB:`, pb, 'year:', year);
      debugLog(`PDF Generator - ${distance} recent PB type:`, typeof pb);
      
      // Check if this is a world record time that shouldn't be displayed
      if (distance === '5K' && pb === '12:35') {
        debugLog('PDF Generator - Detected world record 5K time in recent PBs, setting to null');
        recentPBs.push(`${distance}: null`);
        return;
      }
      
      if (pb && pb !== null && pb !== undefined && pb !== '') {
        debugLog(`PDF Generator - Before formatTime - ${distance} recent PB:`, pb, 'Type:', typeof pb);
        const timeStr = formatTime(pb);
        debugLog(`PDF Generator - After formatTime - ${distance} recent formatted time:`, timeStr);
        
        // Force conversion if formatTime didn't work but we have a number
        if (timeStr === null && typeof pb === 'number' && pb > 0) {
          const hours = Math.floor(pb / 3600);
          const minutes = Math.floor((pb % 3600) / 60);
          const seconds = pb % 60;
          const paddedSeconds = seconds.toString().padStart(2, '0');
          const paddedMinutes = minutes.toString().padStart(2, '0');
          const forcedTime = hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
          debugLog(`PDF Generator - Force converted ${distance} recent time:`, forcedTime);
          const yearStr = year ? ` (${year})` : '';
          recentPBs.push(`${distance}: ${forcedTime}${yearStr}`);
        } else if (timeStr) {
          const yearStr = year ? ` (${year})` : '';
          recentPBs.push(`${distance}: ${timeStr}${yearStr}`);
        } else {
          recentPBs.push(`${distance}: ${pb}${year ? ` (${year})` : ''}`);
        }
      } else {
        recentPBs.push(`${distance}: null`);
      }
    });
    sections.push(`Recent PB (12mo): ${recentPBs.join(', ')}`);
  } else {
    sections.push(`Lifetime PB: null`);
    sections.push(`Recent PB (12mo): null`);
  }
  
  // Weekly volume
  if (data.weeklyVolume) {
    const vol = data.weeklyVolume;
    if (vol.type === 'distance' && vol.value && vol.unit) {
      sections.push(`Weekly Volume: ${vol.value} ${vol.unit}`);
    } else if (vol.type === 'time' && vol.minutes !== undefined) {
      const hours = Math.floor(vol.minutes / 60);
      const minutes = vol.minutes % 60;
      sections.push(`Weekly Volume: ${hours}h ${minutes}m`);
    } else {
      sections.push(`Weekly Volume: null`);
    }
  } else {
    sections.push(`Weekly Volume: null`);
  }
  
  // Target race - comprehensive display
  if (data.targetRace) {
    const race = data.targetRace;
    debugLog('PDF Generator - Target Race data:', race);
    debugLog('PDF Generator - Target Race structure:', JSON.stringify(race, null, 2));
    if (race.distance) {
      if (race.justFinish) {
        sections.push(`Target Race: ${race.distance} (just finish)`);
      } else if (race.targetTime) {
        debugLog('PDF Generator - Target time data:', race.targetTime);
        debugLog('PDF Generator - Target time type:', typeof race.targetTime);
        const timeStr = formatTime(race.targetTime);
        debugLog('PDF Generator - Formatted target time:', timeStr);
        if (timeStr) {
          sections.push(`Target Race: ${race.distance} in ${timeStr}`);
        } else {
          sections.push(`Target Race: ${race.distance}`);
        }
      } else {
        sections.push(`Target Race: ${race.distance}`);
      }
      
      // Race date
      if (race.raceDate) {
        sections.push(`Goal Race Date: ${race.raceDate}`);
      } else {
        sections.push(`Goal Race Date: null`);
      }
    } else {
      sections.push(`Target Race: null`);
      sections.push(`Goal Race Date: null`);
    }
  } else {
    sections.push(`Target Race: null`);
    sections.push(`Goal Race Date: null`);
  }
  
  // Weekly days and training days
  if (data.weeklyDays !== undefined) {
    sections.push(`Weekly Running Days: ${data.weeklyDays} days`);
  } else {
    sections.push(`Weekly Running Days: null`);
  }
  
  // Selected days (specific days of the week)
  if (data.selectedDays && data.selectedDays.length > 0) {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const selectedDayNames = data.selectedDays.map(i => dayNames[i]).filter(Boolean);
    if (selectedDayNames.length > 0) {
      sections.push(`Training Days: ${selectedDayNames.join(', ')}`);
    } else {
      sections.push(`Training Days: null`);
    }
  } else {
    sections.push(`Training Days: null`);
  }
  
  // Training start date
  if (data.trainingStartDate) {
    sections.push(`Training Start Date: ${data.trainingStartDate}`);
  } else {
    sections.push(`Training Start Date: null`);
  }
  
  // Can run twice a day
  if (data.canRunTwice !== undefined) {
    sections.push(`Can Train Doubles: ${data.canRunTwice ? 'Yes' : 'No'}`);
  } else {
    sections.push(`Can Train Doubles: null`);
  }
  
  // Irregular schedule
  if (data.irregularSchedule !== undefined) {
    sections.push(`Irregular Schedule: ${data.irregularSchedule ? 'Yes' : 'No'}`);
  } else {
    sections.push(`Irregular Schedule: null`);
  }
  
  // Training preferences - show all possible options
  const allPreferences = [
    'past_or_current_injury',
    'training_in_another_sport', 
    'pregnant_or_postpartum',
    'returning_after_long_break',
    'let_me_talk_to_coach'
  ];
  
  const preferenceLabels = {
    'past_or_current_injury': 'Past or current injury',
    'training_in_another_sport': 'Training in another sport',
    'pregnant_or_postpartum': 'Pregnant or postpartum',
    'returning_after_long_break': 'Returning after a long break',
    'let_me_talk_to_coach': 'Let me talk to the coach'
  };
  
  const selectedPreferences = data.preferences || [];
  const preferenceTexts = allPreferences.map(pref => {
    const isSelected = selectedPreferences.includes(pref);
    return `${preferenceLabels[pref]}: ${isSelected ? 'Yes' : 'null'}`;
  });
  sections.push(`Training Preferences: ${preferenceTexts.join(', ')}`);
  
  // Check-in frequency
  if (data.checkInFrequency) {
    const frequencies = {
      'every-run': 'After every run',
      'twice-week': 'Twice a week',
      'once-week': 'Once a week',
      'missed-run': 'Only if missed a run',
      'other': 'Other preference'
    };
    const freq = frequencies[data.checkInFrequency] || data.checkInFrequency;
    sections.push(`Check-in Preference: ${freq}`);
  } else {
    sections.push(`Check-in Preference: null`);
  }
  
  // Additional notes
  if (data.additionalNotes) {
    sections.push(`Additional Notes: ${data.additionalNotes}`);
  } else {
    sections.push(`Additional Notes: null`);
  }
  
  debugLog('formatOnboardingData - Final sections:', sections);
  return sections.length > 0 ? sections.join('\n') : null;
};

export const generateTrainingPlanPDF = (regimens = [], onboardingData = null) => {
  try {
    debugLog('PDF Generator - Received regimens:', regimens);
    debugLog('PDF Generator - Regimens length:', regimens.length);
    debugLog('PDF Generator - First regimen structure:', regimens[0]);
    
    // Create PDF with minimal settings
    const doc = new jsPDF();
    let y = 20;
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Date parsing helpers to avoid UTC timezone shifts
    const parseYMDLocal = (s) => {
      if (!s) return null;
      const ymd = String(s).split('T')[0]; // drop any time part
      const [y, m, d] = ymd.split('-').map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    };

    const addDaysLocal = (date, days) => {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      d.setDate(d.getDate() + days);
      return d;
    };
    
    // Simple helper functions
    const addLine = (text, fontSize = 10, bold = false) => {
      if (!text) return;
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      
      // Handle page breaks
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      // Simple text wrapping
      const width = pageWidth - 2 * margin;
      const lines = doc.splitTextToSize(String(text).trim(), width);
      doc.text(lines, margin, y);
      y += lines.length * (fontSize * 0.5) + 3;
    };
    
    // TITLE
    doc.setTextColor(0, 0, 0);
    addLine('RUNIUM TRAINING PLAN', 18, true);
    y += 10;
    
    // Add onboarding data section if available
    debugLog('PDF Generator - Onboarding data received:', onboardingData);
    debugLog('PDF Generator - Onboarding data type:', typeof onboardingData);
    debugLog('PDF Generator - Onboarding data keys:', onboardingData ? Object.keys(onboardingData) : 'N/A');
    
    if (onboardingData) {
      debugLog('PDF Generator - Calling formatOnboardingData...');
      const formattedData = formatOnboardingData(onboardingData);
      debugLog('PDF Generator - Formatted data result:', formattedData);
      debugLog('PDF Generator - Formatted data type:', typeof formattedData);
      debugLog('PDF Generator - Formatted data length:', formattedData ? formattedData.length : 'N/A');
      
      if (formattedData) {
        debugLog('PDF Generator - Adding onboarding data to PDF...');
        // Add section header
        doc.setTextColor(59, 130, 246); // Blue color
        addLine('YOUR PROFILE', 12, true);
        y += 5;
        
        // Add onboarding data
        doc.setTextColor(0, 0, 0);
        const lines = formattedData.split('\n');
        debugLog('PDF Generator - Adding lines to PDF:', lines);
        lines.forEach(line => {
          addLine(line, 9);
        });
        
        // Add separator line
        y += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 10;
        debugLog('PDF Generator - Onboarding data section added successfully');
      } else {
        debugLog('PDF Generator - No formatted data generated from onboarding data');
        // Add a note that profile data is not available
        doc.setTextColor(100, 100, 100);
        addLine('Profile information not available', 9);
        y += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 10;
      }
    } else {
      debugLog('PDF Generator - No onboarding data provided');
      // Add a note that profile data is not available
      doc.setTextColor(100, 100, 100);
      addLine('Profile information not available', 9);
      y += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
    }
    
    // Sort regimens by date using local parsing and deduplicate by start_of_week_date
    const sortedRegimens = [...(regimens || [])].sort((a, b) => {
      const dateA = parseYMDLocal(a.start_of_week_date);
      const dateB = parseYMDLocal(b.start_of_week_date);
      if (!dateA || !dateB) return 0;
      return dateA - dateB;
    });
    
    // Deduplicate regimens by start_of_week_date (keep the first one for each week)
    const uniqueRegimens = [];
    const seenWeeks = new Set();
    
    for (const regimen of sortedRegimens) {
      const startDate = regimen.start_of_week_date;
      if (!seenWeeks.has(startDate)) {
        seenWeeks.add(startDate);
        uniqueRegimens.push(regimen);
        debugLog(`PDF Generator - Added unique regimen for week: ${startDate}`);
      } else {
        debugLog(`PDF Generator - Skipping duplicate regimen for week: ${startDate}`);
      }
    }
    
    debugLog(`PDF Generator - Original regimens: ${sortedRegimens.length}, Unique regimens: ${uniqueRegimens.length}`);
    debugLog(`PDF Generator - Unique regimens data:`, uniqueRegimens.map(r => ({
      id: r.id,
      start_of_week_date: r.start_of_week_date,
      week_header: r.week_header
    })));
    
    if (uniqueRegimens.length === 0) {
      addLine('No training data available', 12);
      doc.save('empty-training-plan.pdf');
      return;
    }
    
    // For each week
    uniqueRegimens.forEach((regimen, weekIdx) => {
      debugLog(`PDF Generator - Processing week ${weekIdx + 1}:`, regimen);
      debugLog(`PDF Generator - regimen_titles:`, regimen.regimen_titles);
      debugLog(`PDF Generator - regimen_subtitles:`, regimen.regimen_subtitles);
      debugLog(`PDF Generator - regimen_details:`, regimen.regimen_details);
      debugLog(`PDF Generator - start_of_week_date:`, regimen.start_of_week_date);
      
      // Generate week header from date (don't use subtitles as fallback)
      const weekHeader = regimen.week_header || (() => {
        const weekStart = parseYMDLocal(regimen.start_of_week_date);
        if (!weekStart) {
          debugError('Invalid start_of_week_date for regimen:', regimen.start_of_week_date);
          return `WEEK ${weekIdx + 1}: Invalid Date`;
        }
        const weekEnd = addDaysLocal(weekStart, 6);
        return `WEEK ${weekIdx + 1}: ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      })();
      
      debugLog(`PDF Generator - Generated week header: ${weekHeader}`);
      
      y += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
      
      // WEEK HEADER
      doc.setTextColor(0, 0, 150);
      debugLog(`PDF Generator - Adding week header for week ${weekIdx + 1}: ${weekHeader}`);
      addLine(weekHeader, 14, true);
      
      // Each day of the week
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      days.forEach((day, dayIdx) => {
        const title = regimen.regimen_titles?.[dayIdx] || 'Rest Day';
        const subtitle = regimen.regimen_subtitles?.[dayIdx] || '';
        
        debugLog(`PDF Generator - Day ${day} (${dayIdx}): title="${title}", subtitle="${subtitle}"`);
        
        // Calculate the actual date for this day
        const weekStart = parseYMDLocal(regimen.start_of_week_date);
        let dayDate = '';
        if (weekStart) {
          const actualDate = addDaysLocal(weekStart, dayIdx);
          dayDate = format(actualDate, 'MMM d');
        }
        
        // New page if needed
        if (y > 240) {
          doc.addPage();
          y = 20;
        }
        
        y += 8;
        
        // DAY HEADER with date
        doc.setTextColor(0, 0, 0);
        const dayHeader = dayDate ? `${day.toUpperCase()}, ${dayDate}: ${title}` : `${day.toUpperCase()}: ${title}`;
        debugLog(`PDF Generator - Adding day header: ${dayHeader}`);
        addLine(dayHeader, 12, true);
        
        if (subtitle) {
          debugLog(`PDF Generator - Adding subtitle: ${subtitle}`);
          doc.setTextColor(100, 100, 100);
          addLine(subtitle, 10);
        }
        
        // If we have details, add them with proper formatting
        const details = regimen.regimen_details?.[dayIdx];
        if (details) {
          // Try to parse as JSON first (new structured format)
          try {
            let jsonString = details.trim();
            
            // Remove common prefixes and suffixes
            if (jsonString.startsWith('json ')) {
              jsonString = jsonString.substring(5);
            }
            if (jsonString.startsWith('```json')) {
              jsonString = jsonString.substring(7);
            }
            if (jsonString.endsWith('```')) {
              jsonString = jsonString.substring(0, jsonString.length - 3);
            }
            
            const structuredData = JSON.parse(jsonString.trim());
            if (structuredData.sections) {
              // Render structured JSON data
              structuredData.sections.forEach(section => {
                if (section.type === 'header') {
                  doc.setTextColor(0, 0, 0);
                  addLine(section.content, 10, true);
                } else if (section.type === 'list' && section.items) {
                  section.items.forEach(item => {
                    doc.setTextColor(80, 80, 80);
                    // Clean up markdown formatting
                    const cleanContent = item.content
                      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
                      .replace(/\*(.*?)\*/g, '$1')     // Remove italic markdown
                      .trim();
                    addLine(`• ${cleanContent}`, 9);
                  });
                } else if (section.type === 'paragraph') {
                  doc.setTextColor(80, 80, 80);
                  // Clean up markdown formatting
                  const cleanContent = section.content
                    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
                    .replace(/\*(.*?)\*/g, '$1')     // Remove italic markdown
                    .trim();
                  addLine(cleanContent, 9);
                }
              });
            } else {
              throw new Error('No sections found in JSON');
            }
          } catch (e) {
            // Fallback to markdown cleaning for existing data
            debugLog('PDF: Parsing as markdown fallback for:', details.substring(0, 100));
            const cleaned = details
              .replace(/Ø\S*/g, '') // Remove all Ø tokens
              .replace(/&\S*/g, '') // Remove all & tokens
              .replace(/##/g, '')   // Remove markdown headers
              .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown but keep text
              .replace(/\*(.*?)\*/g, '$1')     // Remove italic markdown but keep text
              .trim();
            
            if (cleaned) {
              doc.setTextColor(80, 80, 80);
              addLine(cleaned, 9);
            }
          }
         } else if (!regimen.regimen_titles?.[dayIdx]) {
           doc.setTextColor(100, 100, 100);
           addLine('Rest day', 9);
         }
        
        y += 3;
      });
    });
    
    // Calculate detailed statistics
    const totalWeeks = uniqueRegimens.length;
    
    // Count active training days and rest days
    let totalActiveDays = 0;
    let totalRestDays = 0;
    
    debugLog('PDF Generator - Calculating statistics for regimens:', uniqueRegimens);
    
    uniqueRegimens.forEach((regimen, weekIndex) => {
      debugLog(`PDF Generator - Week ${weekIndex + 1} regimen:`, regimen);
      
      // Count each day of the week using the correct array structure
      // regimen_titles[0] = Monday, regimen_titles[1] = Tuesday, etc.
      const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      daysOfWeek.forEach((dayName, dayIndex) => {
        const title = regimen.regimen_titles?.[dayIndex];
        if (title && title.trim() !== '') {
          const titleLower = title.toLowerCase();
          debugLog(`PDF Generator - ${dayName} data:`, { title, dayIndex });
          
          // Count days that have actual content
          if (titleLower.includes('rest') || titleLower.includes('off') || titleLower.includes('recovery') || titleLower.includes('rest day')) {
            totalRestDays++;
            debugLog(`PDF Generator - Counted rest day: ${title}`);
          } else {
            totalActiveDays++;
            debugLog(`PDF Generator - Counted training day: ${title}`);
          }
        }
        // Don't count implicit rest days - only count actual scheduled days
      });
    });
    
    debugLog(`PDF Generator - Final counts: ${totalActiveDays} training, ${totalRestDays} rest`);
    
    const totalDays = totalActiveDays + totalRestDays;
    
    // Save the PDF
    const filename = `runium-training-plan-${totalWeeks}-weeks.pdf`;
    doc.save(filename);
    
    // Show detailed success message
    toastSuccess(`PDF saved: ${totalWeeks} week${totalWeeks === 1 ? '' : 's'}`);
    
  } catch (error) {
    debugError('Error generating PDF:', error);
    toastError('Could not generate PDF. Please try again.');
  }
};